import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getTenantFromCache, setTenantCache } from '../services/tenantCache';

// Interface pour les données du tenant
export interface TenantData {
  id: string;
  name: string;
  email: string;
  logo: string;
  domain_name: string;
  config?: any;
  [key: string]: any; // Pour permettre l'accès à d'autres propriétés
}

/**
 * Hook personnalisé pour accéder aux données du tenant avec cache
 * @returns Un objet contenant les données du tenant et l'état de chargement
 */
export function useTenant() {
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTenantData() {
      try {
        // Résolution dynamique du tenant par domaine
        const currentDomain = window.location.hostname;
        console.log('🌐 Résolution du tenant pour le domaine:', currentDomain);
        
        // Tentative de résolution par domaine
        const { data: resolvedData, error: resolveError } = await supabase
          .rpc('resolve_tenant_by_domain', { domain_input: currentDomain });

        if (!resolveError && resolvedData && resolvedData.length > 0) {
          const resolved = resolvedData[0];
          console.log('✅ Tenant résolu:', resolved.tenant_id, resolved.tenant_name);
          
          // Utiliser la configuration fusionnée
          const tenantData: TenantData = {
            id: resolved.tenant_id.toString(),
            name: resolved.merged_config?.name || resolved.tenant_name,
            email: resolved.merged_config?.email || `contact@${currentDomain}`,
            logo: resolved.merged_config?.logo || '',
            domain_name: currentDomain,
            config: resolved.merged_config || {},
            merged_config: resolved.merged_config
          };
          
          setTenant(tenantData);
          setTenantCache(tenantData);
          setLoading(false);
          return;
        }
        
        // Fallback vers VITE_TENANT_ID si la résolution échoue
        console.log('⚠️ Résolution par domaine échouée, fallback vers VITE_TENANT_ID');
        const tenantId = import.meta.env.VITE_TENANT_ID || '1';
        
        // Tentative de récupération depuis le cache
        const cachedTenant = getTenantFromCache(tenantId);
        if (cachedTenant) {
          setTenant(cachedTenant);
          setLoading(false);
          return;
        }

        // Si pas de cache, récupérer depuis Supabase
        console.log('🌐 Récupération des données tenant depuis Supabase...');
        const { data, error } = await supabase
          .from('tenants')
          .select('*')
          .eq('id', tenantId)
          .single();

        if (error) {
          console.error('Erreur lors de la récupération du tenant:', error);
          setError(error.message);
          // Valeurs par défaut en cas d'erreur
          const defaultTenant: TenantData = {
            id: tenantId,
            name: 'Default Tenant',
            email: 'contact@example.com',
            logo: '',
            domain_name: 'example.com',
            config: {}
          };
          setTenant(defaultTenant);
          // Ne pas mettre en cache les données par défaut
        } else {
          // Traitement des données du tenant
          const tenantData: TenantData = {
            id: data.id,
            name: data.name || 'Tenant',
            email: data.email || `contact@${data.domain_name}`,
            logo: data.logo || '',
            domain_name: data.domain_name || 'example.com',
            config: data.config || {},
            ...data // Inclure toutes les autres propriétés
          };
          
          setTenant(tenantData);
          // Sauvegarder en cache pour les prochaines utilisations
          setTenantCache(tenantData);
        }
      } catch (err: any) {
        console.error('Erreur inattendue:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchTenantData();
  }, []);

  return { tenant, loading, error };
}

/**
 * Fonction utilitaire pour formater les chaînes avec les valeurs du tenant
 * @param template La chaîne template avec des placeholders comme {name}, {email}, etc.
 * @param tenantData Les données du tenant
 * @returns La chaîne formatée avec les valeurs du tenant
 */
export function formatWithTenant(template: string, tenantData: TenantData | null): string {
  if (!tenantData) return template;
  
  return template.replace(/{([^}]+)}/g, (match, key) => {
    const value = key.split('.').reduce((obj: any, prop: string) => {
      return obj && obj[prop] !== undefined ? obj[prop] : undefined;
    }, tenantData);
    
    return value !== undefined ? String(value) : match;
  });
}
